/**
 * Custom hook for OneDrive/Microsoft OAuth authentication via central auth domain popup
 * Handles cross-domain communication using postMessage API
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
    GooglePickerMessage,
    isValidOrigin,
    getMainDomain,
    openCenteredPopup
} from "../google-picker/google-picker-message.util";

interface UseOneDriveAuthPopupOptions {
    /** Expected email for validation (optional) */
    expectedEmail?: string;
    /** Callback when authentication is successful */
    onSuccess?: (accessToken: string, email: string) => void;
    /** Callback when authentication is cancelled */
    onCancel?: () => void;
    /** Callback on error */
    onError?: (error: string) => void;
    /** Whether the hook is enabled (sets up message listener). Default true. */
    enabled?: boolean;
}

interface UseOneDriveAuthPopupReturn {
    /** Opens the OneDrive Auth popup */
    openAuth: () => void;
    /** Whether the auth popup is currently open */
    isOpen: boolean;
    /** Loading state while waiting for auth */
    isLoading: boolean;
}

/**
 * Gets the OneDrive Auth URL on the auth domain
 */
const getAuthUrl = (expectedEmail?: string): string => {
    const hostname = window.location.hostname;

    // Handle localhost development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        const baseUrl = `http://localhost:${window.location.port || 3000}/onedrive-auth`;
        const params = new URLSearchParams({
            returnOrigin: window.location.origin
        });
        if (expectedEmail) {
            params.set("expectedEmail", expectedEmail);
        }
        return `${baseUrl}?${params.toString()}`;
    }

    const authDomain = process.env.REACT_APP_LOGIN_DOMAIN;
    if (!authDomain) {
        throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
    }

    const params = new URLSearchParams({
        returnOrigin: window.location.origin
    });
    if (expectedEmail) {
        params.set("expectedEmail", expectedEmail);
    }

    return `${authDomain}/onedrive-auth?${params.toString()}`;
};

/**
 * Hook for managing OneDrive Auth popup with cross-domain communication
 */
export const useOneDriveAuthPopup = (
    options: UseOneDriveAuthPopupOptions = {}
): UseOneDriveAuthPopupReturn => {
    const { expectedEmail, onSuccess, onCancel, onError, enabled = true } = options;

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const popupRef = useRef<Window | null>(null);
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, []);

    // Handle incoming messages from the auth popup
    const handleMessage = useCallback(
        (event: MessageEvent) => {
            console.log("useOneDriveAuthPopup: Received message from origin:", event.origin);

            // Validate origin
            const mainDomain = getMainDomain();
            if (!isValidOrigin(event.origin, [mainDomain])) {
                console.log("useOneDriveAuthPopup: Origin validation failed, ignoring message");
                return;
            }

            const message = event.data as GooglePickerMessage;

            if (!message || !message.type) {
                console.log("useOneDriveAuthPopup: Invalid message format, ignoring");
                return;
            }

            console.log("useOneDriveAuthPopup: Valid message received, type:", message.type);

            switch (message.type) {
                case "GOOGLE_PICKER_READY":
                    console.log("useOneDriveAuthPopup: Auth page is ready");
                    setIsLoading(false);
                    break;

                case "GOOGLE_PICKER_RESULT":
                    console.log("useOneDriveAuthPopup: Got auth result", message.payload);
                    if (message.payload) {
                        if (message.payload.success && message.payload.accessToken) {
                            // url field is repurposed for email
                            const email = message.payload.url || "";
                            console.log(
                                "useOneDriveAuthPopup: Calling onSuccess with token and email:",
                                email
                            );
                            onSuccess?.(message.payload.accessToken, email);
                        } else if (message.payload.error) {
                            console.log(
                                "useOneDriveAuthPopup: Calling onError:",
                                message.payload.error
                            );
                            onError?.(message.payload.error);
                        }
                    }
                    setIsOpen(false);
                    setIsLoading(false);
                    break;

                case "GOOGLE_PICKER_CANCEL":
                    console.log("useOneDriveAuthPopup: Auth was cancelled");
                    onCancel?.();
                    setIsOpen(false);
                    setIsLoading(false);
                    break;
            }
        },
        [onSuccess, onCancel, onError]
    );

    // Set up message listener (only when enabled)
    useEffect(() => {
        if (!enabled) {
            return;
        }
        console.log("useOneDriveAuthPopup: Setting up message listener");
        window.addEventListener("message", handleMessage);
        return () => {
            console.log("useOneDriveAuthPopup: Removing message listener");
            window.removeEventListener("message", handleMessage);
        };
    }, [handleMessage, enabled]);

    // Monitor popup closure (user closing window manually)
    useEffect(() => {
        if (isOpen && popupRef.current) {
            checkIntervalRef.current = setInterval(() => {
                if (popupRef.current?.closed) {
                    setIsOpen(false);
                    setIsLoading(false);
                    if (checkIntervalRef.current) {
                        clearInterval(checkIntervalRef.current);
                        checkIntervalRef.current = null;
                    }
                }
            }, 500);
        }

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
        };
    }, [isOpen]);

    const openAuth = useCallback(() => {
        try {
            const url = getAuthUrl(expectedEmail);
            console.log("useOneDriveAuthPopup: Opening auth popup:", url);

            const popup = openCenteredPopup(url, "OneDriveAuthPopup", 500, 600);

            if (popup) {
                popupRef.current = popup;
                setIsOpen(true);
                setIsLoading(true);
            } else {
                onError?.("Popup was blocked. Please allow popups for this site.");
            }
        } catch (error) {
            onError?.(error instanceof Error ? error.message : "Failed to open auth popup");
        }
    }, [expectedEmail, onError]);

    return {
        openAuth,
        isOpen,
        isLoading
    };
};

export default useOneDriveAuthPopup;
